// src/pages/MatchesPage.jsx
import React, { useMemo } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useProtocolShell } from "@/contexts/ProtocolShellContext";
import { toast } from "sonner";
import ComboboxBasic from "@/components/ComboboxBasic";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, Clock3, MapPin, ArrowRight, AlertTriangle, History, Search } from "lucide-react";
import { LoadingBlock } from "@/components/LoadingState";
import { useMatches, useOfficialMatches } from "@/queries/matches";
import PageTutorial from "@/components/tutorial/PageTutorial";
import { formatSvDateTime } from "@/utils/date";
import Seo from "@/components/Seo";
import { getNativePlatform } from "@/lib/runtime";
import { NativeMatchGridSkeleton } from "@/components/skeletons/NativeLoadingSkeletons";
import TeamLogo from "@/components/TeamLogo";

import {
  describeMatchStart,
  getMatchVenue,
  parseMatchDate,
  toDateKey,
  getMatchTeams,
} from "@/utils/matchFormatting";
import { asArray } from "@/lib/asArray";
import { findExistingProtocolForOfficialMatch } from "@/lib/protocolMatches";

function isManualCancelled(match) {
  return String(match?.manual_status || "").trim().toLowerCase() === "cancelled";
}

function NativeArchiveMatchRow({ match, onOpen }) {
  const { dateLabel, timeLabel } = describeMatchStart(match);
  const { home, away } = getMatchTeams(match);
  const cancelled = isManualCancelled(match);
  const hasScore = match?.home_score != null
    && match?.away_score != null
    && Number.isFinite(Number(match.home_score))
    && Number.isFinite(Number(match.away_score));

  return (
    <button type="button" className="native-v3-match-browser-row" onClick={() => onOpen(match.idStr)}>
      <span className="native-v3-match-browser-date">
        <strong>{dateLabel || "Datum saknas"}</strong>
        <small>{cancelled ? "Inställd" : timeLabel || (hasScore ? "Slutresultat" : "Tid ej klar")}</small>
      </span>
      <span className="native-v3-match-browser-teams">
        <span><TeamLogo teamName={home} size={28} /><strong>{home}</strong></span>
        <span><TeamLogo teamName={away} size={28} /><strong>{away}</strong></span>
      </span>
      <span className="native-v3-match-browser-result">
        {hasScore ? <strong>{Number(match.home_score)}–{Number(match.away_score)}</strong> : <ArrowRight />}
      </span>
    </button>
  );
}

// import { Calendar } from "lucide-react"; // bara om du visar matchlistan nedan

// TanStack hooks
// import {
//   useMatches,
//   useOfficialMatches,
//   useCreateFromOfficial,
// } from "@/queries/matches";



export default function MatchesPage({ embedded = false, onOpenOfficial = null }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { requestOfficialShell, requestProtocolSession } = useProtocolShell();
  const isNativeAndroid = getNativePlatform() === "android";

  // Egna skapade matcher (valfritt att visa nedan)
  // const { data: matches = [] } = useMatches({ staleTime: Infinity });

  const { data: matches = [] } = useMatches({
    staleTime: Infinity,
    enabled: !!user,
  });

  const consumedOfficialIds = useMemo(() => {
    const list = asArray(matches);
    const ids = list
      .map((match) => match?.official_match_id || match?.officialMatchId)
      .filter(Boolean)
      .map((id) => String(id));
    return new Set(ids);
  }, [matches]);

  // Hämta officiella matcher
  const {
    data: officialRaw = [],
    isLoading,
    error,
  } = useOfficialMatches({
    enabled: !!user,
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    // select: (xs) => [...xs].sort((a, b) => new Date(a.date) - new Date(b.date)),
  });

  // Normalisera id:n för att kunna använda hela listan i t.ex. sökfältet
  const normalizedOfficial = useMemo(
    () =>
      officialRaw
        .map((m) => ({
          ...m,
          idStr: String(m?.id ?? m?._id ?? ""),
          __parsedDate: parseMatchDate(m),
        }))
        .sort((a, b) => {
          const aTime = a.__parsedDate?.getTime() ?? Infinity;
          const bTime = b.__parsedDate?.getTime() ?? Infinity;
          return aTime - bTime;
        }),
    [officialRaw]
  );

  // Begränsad lista för kort och kommande matcher
  const official = useMemo(() => {
    const today = new Date();
    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    return normalizedOfficial
      .filter((match) => {
        if (!embedded) return true;
        const rawId = match?.id ?? match?._id;
        if (!rawId) return true;
        const officialId = String(rawId);
        if (consumedOfficialIds.has(officialId)) {
          return false;
        }
        return !match?.consumedByCurrentUser;
      })
      
      .filter((m) => {
        const dt = m.__parsedDate;
        if (!dt) return true;
        const matchDay = new Date(dt.getFullYear(), dt.getMonth(), dt.getDate());
        return matchDay >= startOfToday;
      });
  }, [normalizedOfficial, consumedOfficialIds]);

  const nextDateKey = useMemo(() => {
    const firstWithDate = official.find((m) => m.__parsedDate);
    return toDateKey(firstWithDate?.__parsedDate ?? null);
  }, [official]);



  const featuredMatches = useMemo(() => {
    if (!nextDateKey) {
      return official.slice(0, 1);
    }
    return official.filter((m) => toDateKey(m.__parsedDate) === nextDateKey);
  }, [official, nextDateKey]);

  const featuredIds = useMemo(
    () => new Set(featuredMatches.map((match) => match.idStr)),
    [featuredMatches]
  );

  // const createFromOfficial = useCreateFromOfficial();


  const upcomingList = useMemo(
    () => official.filter((m) => !featuredIds.has(m.idStr)).slice(0, 8),
    [official, featuredIds]
  );

  const nativeArchive = useMemo(() => {
    const now = new Date();
    const withDates = normalizedOfficial.filter((match) => match.__parsedDate);
    return {
      upcoming: withDates
        .filter((match) => match.__parsedDate >= now)
        .slice(0, 6),
      previous: withDates
        .filter((match) => match.__parsedDate < now)
        .sort((a, b) => b.__parsedDate.getTime() - a.__parsedDate.getTime())
        .slice(0, 8),
    };
  }, [normalizedOfficial]);

  // const onSelectOfficial = async (officialIdStr) => {
  //   setSelectedOfficialId(officialIdStr);
  //   const m = official.find((x) => x.idStr === officialIdStr);
  //   if (!m) return;

  const comboboxItems = useMemo(
    () =>
      normalizedOfficial.map((m) => {
        const { dateLabel, timeLabel } = describeMatchStart(m);
        const { home, away } = getMatchTeams(m);
        return {
          id: m.idStr,
          label: `${home} vs ${away}`,
          sublabel: timeLabel
            ? `${timeLabel} • ${dateLabel}`
            : dateLabel ?? "Datum ej klart",
          raw: m,
        };
      }),
    [normalizedOfficial]
  );

  const handleNavigate = (id) => {
    if (!id) return;
    if (embedded && typeof onOpenOfficial === "function") {
      onOpenOfficial(id);
      return;
    }
    const existingMatch = findExistingProtocolForOfficialMatch(matches, id);
    if (existingMatch?.id) {
      requestProtocolSession(existingMatch.id);
      return;
    }
    requestOfficialShell(id);
  };

  if (isNativeAndroid) {
    return (
      <div className="native-v3-match-browser">
        <Seo
          title="Matcher"
          description="Sök bland tidigare och kommande speedwaymatcher."
          canonicalPath="/matches"
        />
        <PageTutorial pageName="matches" />

        <header className="native-v3-match-browser-heading">
          <span>Matcharkiv</span>
          <h2>Hela matchlistan</h2>
          <p>Sök i hela schemat eller bläddra bland kommande och tidigare matcher.</p>
        </header>

        <section className="native-v3-match-browser-search" aria-labelledby="native-match-search-title">
          <div>
            <Search aria-hidden="true" />
            <span>
              <small>Hela schemat</small>
              <strong id="native-match-search-title">Hitta en match</strong>
            </span>
          </div>
          <ComboboxBasic
            items={comboboxItems}
            value={undefined}
            onChange={handleNavigate}
            disabled={isLoading || !!error}
            placeholder={isLoading ? "Laddar…" : "Sök lag eller datum"}
            searchPlaceholder="Sök efter lag eller datum"
            emptyText="Inga matcher hittades"
            className="native-v3-match-browser-search-control"
            maxHeightRem={22}
          />
        </section>

        {isLoading ? (
          <NativeMatchGridSkeleton />
        ) : error ? (
          <div className="native-v3-match-browser-empty">Kunde inte hämta matchlistan.</div>
        ) : (
          <>
            <section className="native-v3-match-browser-section">
              <div className="native-v3-match-browser-section-heading">
                <CalendarDays aria-hidden="true" />
                <span><small>Framåt i schemat</small><h3>Kommande matcher</h3></span>
              </div>
              <div className="native-v3-match-browser-list">
                {nativeArchive.upcoming.length ? nativeArchive.upcoming.map((match) => (
                  <NativeArchiveMatchRow key={`future-${match.idStr}`} match={match} onOpen={handleNavigate} />
                )) : <div className="native-v3-match-browser-empty">Inga kommande matcher är publicerade just nu.</div>}
              </div>
            </section>

            <section className="native-v3-match-browser-section">
              <div className="native-v3-match-browser-section-heading">
                <History aria-hidden="true" />
                <span><small>Matcharkiv</small><h3>Senaste matcher</h3></span>
              </div>
              <div className="native-v3-match-browser-list">
                {nativeArchive.previous.length ? nativeArchive.previous.map((match) => (
                  <NativeArchiveMatchRow key={`past-${match.idStr}`} match={match} onOpen={handleNavigate} />
                )) : <div className="native-v3-match-browser-empty">Det finns inga tidigare matcher att visa.</div>}
              </div>
            </section>
          </>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Seo
        title="Matcher"
        description="Se kommande speedwaymatcher, öppna matchkort och starta eller återuppta ditt digitala matchprotokoll."
        canonicalPath="/matches"
      />
      <PageTutorial pageName="matches" />
      <Card>
        <CardHeader>
          <CardTitle>Nästa matcher</CardTitle>
          <CardDescription>
            Snabb åtkomst till de matcher som står näst på tur. Klicka för att se hela
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {isLoading ? (
              getNativePlatform() === "android" ? <NativeMatchGridSkeleton /> : <LoadingBlock text="Hämtar matcher" />
            ) : error ? (
              <p className="text-red-600">Kunde inte hämta officiella matcher.</p>
            ) : featuredMatches.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                Inga matcher att visa just nu. Försök igen senare.
              </p>
            ) : (
              featuredMatches.map((match) => {
                const { dateLabel, timeLabel, isToday } = describeMatchStart(match);
                const venue = getMatchVenue(match);
                const { home, away } = getMatchTeams(match);
                const isCancelled = isManualCancelled(match);
                return (
                  <button
                    id="featuredMatches"
                    type="button"
                    key={match.idStr}
                    onClick={() => handleNavigate(match.idStr)}
                    className="group rounded-xl border bg-card p-4 text-left transition hover:border-primary/60 hover:shadow-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-lg font-semibold text-foreground">
                          {home}
                          <span className="text-muted-foreground"> vs </span>
                          {away}
                        </h3>
                        <div className="mt-3 space-y-2 text-sm text-muted-foreground">
                          <div className="flex items-center gap-2">
                            <CalendarDays className="h-4 w-4" />
                            <span>
                              {dateLabel}
                            </span>
                          </div>
                          {timeLabel && (
                            <div className="flex items-center gap-2">
                              <Clock3 className="h-4 w-4" />
                              <span>{timeLabel}</span>
                            </div>
                          )}
                          {venue && (
                            <div className="flex items-center gap-2">
                              <MapPin className="h-4 w-4" />
                              <span>{venue}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <Badge variant={isCancelled ? "destructive" : isToday ? "secondary" : "outline"}>
                        {isCancelled ? "Inställd" : isToday ? "Idag" : "Kommande"}
                      </Badge>
                    </div>

                    {isCancelled ? (
                      <div className="mt-4 inline-flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                        <AlertTriangle className="h-4 w-4" />
                        <span>Matchen är inställd</span>
                      </div>
                    ) : null}

                    {/* {officialError && (
                        <div className="mt-2 text-sm text-red-500">
                          Kunde inte hämta officiella matcher.
                        </div>
                      )} */}
                    {/* {typeof match.home_score === "number" &&
                      typeof match.away_score === "number" ? (
                      <div className="mt-4 inline-flex items-center gap-2 rounded-md bg-muted px-3 py-2 text-sm font-semibold">
                        <span>{match.home_score}</span>
                        <span className="text-muted-foreground">-</span>
                        <span>{match.away_score}</span>
                      </div>
                    ) : null} */}
                    <div className="mt-4 flex items-center gap-2 text-sm font-medium text-primary">
                      <span>Öppna matchkort</span>
                      <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" />
                    </div>
                  </button>
                );
              })
            )}

            {/* {selectedOfficial && (
                        <div className="mt-2 text-sm text-muted-foreground">
                          Vald: <strong>{selectedOfficial.home_team}</strong> vs{" "}
                          <strong>{selectedOfficial.away_team}</strong> •{" "}
                          {formatSvDateTime(selectedOfficial.date)}
                        </div>
                      )} */}
          </div>
        </CardContent>
      </Card>



      <Card className="mt-6">
        <CardHeader>
          {/* <CardTitle className="flex items-center">
            <CalendarDays className="w-5 h-5 mr-2" />
            Skapade matcher
          </CardTitle> */}
          <CardTitle>Hitta en annan match</CardTitle>
          <CardDescription>
            Sök i hela schemat och öppna matchkortet för att starta eller
            återuppta ditt protokoll.
          </CardDescription>
        </CardHeader>
        {/* <CardContent>
          <pre className="text-xs bg-muted p-3 rounded">{JSON.stringify(matches, null, 2)}</pre> */}
        <CardContent className="space-y-6">
          <div id="matchFilters">
            <ComboboxBasic
              items={comboboxItems}
              value={undefined}
              onChange={handleNavigate}
              disabled={isLoading || !!error}
              placeholder={
                isLoading
                  ? "Laddar…"
                  : comboboxItems.length
                    ? "Sök och välj match…"
                    : "Inga matcher tillgängliga"
              }
              searchPlaceholder="Sök efter lag eller datum"
              emptyText="Inga matcher hittades"
              className="w-full"
              maxHeightRem={22}
            />
          </div>

          <div id="upcomingMatches" className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                Kommande matcher
              </h3>
            </div>
            <div className="space-y-3">
              {upcomingList.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  Fler matcher visas här när schemat uppdateras.
                </p>
              ) : (
                upcomingList.map((match) => {
                  const { dateLabel, timeLabel } = describeMatchStart(match);
                  const { home, away } = getMatchTeams(match);
                  const isCancelled = isManualCancelled(match);
                  return (
                    <div
                      key={`upcoming-${match.idStr}`}
                      className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3"
                    >
                      <div>
                        <p className="font-medium text-foreground">
                          {home}
                          <span className="text-muted-foreground"> vs </span>
                          {away}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {timeLabel ? `${timeLabel} • ${dateLabel}` : dateLabel}
                        </p>
                        {isCancelled ? (
                          <p className="mt-1 inline-flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-300">
                            <AlertTriangle className="h-4 w-4" />
                            Matchen är inställd
                          </p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleNavigate(match.idStr)}
                      >
                        Öppna
                      </Button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </CardContent>
        {/* </Card> 
      */}
      </Card>
    </div>
  );
}
